import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiFilter, FiSearch, FiPlusCircle, FiX } from "react-icons/fi";
import { SearchX } from "lucide-react";
import MissionCard from "./components/MissionCard";
import {
  listMissions,
  MISSION_TYPES,
  RADIUS_OPTIONS,
} from "../../utils/missionsApi";

export default function MissionsList({ defaultFilters = {} }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const role = (localStorage.getItem("role") || "").toLowerCase();
  const isProvider = role === "partner" || role === "admin";
  const canCreateMission =
    role === "admin" ||
    (role === "pro" && localStorage.getItem("proCanPostMissions") === "true");

  const [filters, setFilters] = useState(() => ({
    q: params.get("q") || "",
    type: params.get("type") || "",
    city: params.get("city") || "",
    urgent: params.get("urgent") === "1" || defaultFilters.urgent === true,
    today: params.get("today") === "1",
    multiJours: params.get("multiJours") === "1",
    convoyage: params.get("convoyage") === "1",
    bodywork: params.get("bodywork") === "1",
    wheels: params.get("wheels") === "1",
    cleaning: params.get("cleaning") === "1",
    minBudget: params.get("minBudget") || "",
    maxBudget: params.get("maxBudget") || "",
    radius: params.get("radius") || "",
  }));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFilters, setOpenFilters] = useState(false);
  const [partnerGate, setPartnerGate] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const q = {
        ...(defaultFilters || {}),
        ...filters,
        status: "ouverte,propositions_recues",
        limit: 40,
      };
      // Remove empties
      Object.keys(q).forEach((k) => {
        if (q[k] === "" || q[k] === false || q[k] === undefined) delete q[k];
        if (q[k] === true) q[k] = "1";
      });
      const data = await listMissions(q);
      const list = data.items || [];
      setPartnerGate(data.partnerGate || null);
      // Light client-side text search on title/desc
      const search = (filters.q || "").trim().toLowerCase();
      setItems(search
        ? list.filter((m) =>
          (m.title || "").toLowerCase().includes(search) ||
          (m.description || "").toLowerCase().includes(search) ||
          (m.companyName || "").toLowerCase().includes(search))
        : list);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filters, defaultFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Sync URL params
  useEffect(() => {
    const next = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== false) next.set(k, v === true ? "1" : String(v));
    });
    setParams(next, { replace: true });
  }, [filters, setParams]);

  const update = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const reset = () => setFilters({
    q: "", type: "", city: "",
    urgent: false, today: false, multiJours: false,
    convoyage: false, bodywork: false, wheels: false, cleaning: false,
    minBudget: "", maxBudget: "", radius: "",
  });

  const activeFilterCount = useMemo(() => {
    return Object.entries(filters).filter(([, v]) => v && v !== "").length;
  }, [filters]);

  return (
    <>
      {partnerGate?.mode === "provider_blocked" && (
        <div
          className="pmm-card"
          style={{
            marginBottom: 8,
            borderColor: "rgba(180, 83, 9, 0.35)",
            background: "linear-gradient(135deg, rgba(254, 243, 199, 0.5), #fff)",
          }}
        >
          <strong>Accès missions limité.</strong>{" "}
          {partnerGate.reason === "no_partner_profile"
            ? "Devenez ProMeet Partner et soumettez votre dossier pour postuler aux missions."
            : partnerGate.reason === "not_approved"
              ? `Votre dossier est : ${partnerGate.status || "en cours"}. Vous serez notifié après validation manuelle.`
              : "Complétez votre profil partenaire pour voir les missions correspondant à vos spécialités."}
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              className="pmm-btn pmm-btn--brand"
              onClick={() => navigate("/auth/signup/partner")}
            >
              Devenir ProMeet Partner
            </button>
            <button
              type="button"
              className="pmm-btn pmm-btn--ghost"
              style={{ marginLeft: 8 }}
              onClick={() => navigate("/partner/mon-dossier")}
            >
              Mon dossier
            </button>
          </div>
        </div>
      )}

      {partnerGate?.mode === "provider" && partnerGate.allowedMissionTypes?.length > 0 && (
        <div className="pmm-chip pmm-chip--brand" style={{ width: "fit-content" }}>
          Filtre actif : {partnerGate.approvedSpecialties?.length || 0} spécialité(s) validée(s)
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="pmm-topbar">
        <div>
          <h1 className="pmm-h-accent">{defaultFilters.urgent ? "Missions urgentes" : "Missions disponibles"}</h1>
          <div className="pmm-topbar-sub">
            {items.length} mission{items.length > 1 ? "s" : ""} {defaultFilters.urgent ? "urgente" : "disponible"}{items.length > 1 ? "s" : ""}.
          </div>
        </div>
        <div className="pmm-topbar-actions">
          <button
            className="pmm-btn pmm-btn--ghost"
            onClick={() => setOpenFilters((v) => !v)}
          >
            <FiFilter /> Filtres{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
          {canCreateMission && (
            <button className="pmm-btn pmm-btn--primary" onClick={() => navigate("/missions/create")}>
              <FiPlusCircle /> Nouvelle mission
            </button>
          )}
        </div>
      </div>

      <div className="pmm-card" style={{ display: openFilters ? "block" : "none" }}>
        <div className="pmm-form">
          <div className="pmm-row pmm-row--3">
            <div className="pmm-field">
              <label>Recherche</label>
              <div style={{ position: "relative" }}>
                <FiSearch style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                <input
                  type="text"
                  placeholder="Titre, description, garage..."
                  value={filters.q}
                  onChange={(e) => update({ q: e.target.value })}
                  style={{ paddingLeft: 34 }}
                />
              </div>
            </div>
            <div className="pmm-field">
              <label>Type</label>
              <select value={filters.type} onChange={(e) => update({ type: e.target.value })}>
                <option value="">Tous les types</option>
                {MISSION_TYPES.map((t) => (
                  <option key={t.key} value={t.key}>{t.icon} {t.label}</option>
                ))}
              </select>
            </div>
            <div className="pmm-field">
              <label>Ville</label>
              <input type="text" placeholder="Paris, Lyon..." value={filters.city}
                     onChange={(e) => update({ city: e.target.value })} />
            </div>
          </div>

          <div className="pmm-row pmm-row--3">
            <div className="pmm-field">
              <label>Budget min (€)</label>
              <input type="number" min={0} placeholder="0" value={filters.minBudget}
                     onChange={(e) => update({ minBudget: e.target.value })} />
            </div>
            <div className="pmm-field">
              <label>Budget max (€)</label>
              <input type="number" min={0} placeholder="—" value={filters.maxBudget}
                     onChange={(e) => update({ maxBudget: e.target.value })} />
            </div>
            <div className="pmm-field">
              <label>Rayon (km)</label>
              <select value={filters.radius} onChange={(e) => update({ radius: e.target.value })}>
                <option value="">Tous</option>
                {RADIUS_OPTIONS.map((r) => <option key={r} value={r}>{r} km</option>)}
              </select>
            </div>
          </div>

          <div className="pmm-pill-row">
            {[
              { key: "urgent",     label: "🔥 Urgent" },
              { key: "today",      label: "📅 Aujourd'hui" },
              { key: "multiJours", label: "🗓️ Multi-jours" },
              { key: "convoyage",  label: "🚛 Convoyage" },
              { key: "bodywork",   label: "🛠️ Carrosserie" },
              { key: "wheels",     label: "🛞 Jantes" },
              { key: "cleaning",   label: "🧽 Nettoyage" },
            ].map((p) => (
              <button
                key={p.key}
                type="button"
                className={`pmm-pill ${filters[p.key] ? "is-active" : ""}`}
                onClick={() => update({ [p.key]: !filters[p.key] })}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="pmm-btn" onClick={reset}><FiX /> Réinitialiser</button>
          </div>
        </div>
      </div>

      {loading ? <div className="pmm-spinner" /> :
        items.length === 0 ? (
          <div className="pmm-empty">
            <div className="pmm-empty-icon"><SearchX size={28} /></div>
            <h3>Aucune mission ne correspond</h3>
            <p>Modifiez vos filtres ou élargissez le rayon de recherche.</p>
            <div style={{ marginTop: 12 }}>
              <button className="pmm-btn pmm-btn--ghost" onClick={reset}>Réinitialiser les filtres</button>
            </div>
          </div>
        ) : (
          <div className="pmm-grid pmm-grid--2">
            {items.map((m) => (
              <MissionCard
                key={m._id}
                mission={m}
                onOpen={() => navigate(`/missions/${m._id}`)}
                onApply={isProvider ? () => navigate(`/missions/${m._id}?apply=1`) : undefined}
              />
            ))}
          </div>
        )}
    </div>
    </>
  );
}
